import _ from 'lodash';
import * as stateUtils from './state';
// import * as constants from '../constants';

// const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').ActivityLog} ActivityLog
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetActivityLogsActionOpts
 * @property {string} organization
 * @property {string} account
 * @property {string} action
 * @property {number} startAt date range
 * @property {number} endAt date range
 * @property {number} limit pagination
 * @property {number} skip pagination
 *
 * @callback GetActivityLogsAction
 * @param {ActionContext} ctx
 * @param {GetActivityLogsActionOpts} opts
 * @returns {Promise<ActivityLog>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetActivityLogsAction}
 */
export const getActivityLogs = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, stateUtils.DEFAULT_LOGS_PAGINATION_OPTS, opts);
  const paginationOpts = _.pick(opts, 'skip', 'limit');

  // fetch
  const query = {
    $limit: opts.limit,
    $skip: !opts.skip ? void 0 : opts.limit * (opts.skip - 1),
    $sort: { createdAt: -1 },
    $populate: {
      _account: {
        key: 'account',
        method: 'findOne',
        service: 'accounts'
      },
      template: {
        service: 'form-templates',
        localKey: 'record.template',
        method: 'get'
      },
      patient: {
        service: 'personal-details',
        method: 'findOne',
        localKey: 'record.patient',
        foreignKey: 'id',
        $select: ['name']
      },
      createdByDetails: {
        service: 'personal-details',
        method: 'findOne',
        localKey: 'record.createdBy',
        foreignKey: 'id',
        $select: ['name']
      },
      createdByMembership: {
        service: 'organization-members',
        idField: 'uid',
        method: 'findOne',
        key: 'createdBy'
      },
      medicalProcedureProviders: {
        service: 'personal-details',
        method: 'find',
        localKey: 'providers',
        foreignKey: 'id'
      },
      ref: {
        service: 'services',
        method: 'get',
        localKey: 'ref'
      },
      results: {
        service: 'medical-records',
        method: 'find',
        localKey: 'id',
        foreignKey: 'order',
        type: {
          $in: [ 'lab-test-result', 'imaging-test-result' ]
        },
        $populate: {
          tests: {
            service: 'diagnostic-tests',
            method: 'find',
            localKey: 'results',
            extractKey: 'test',
            foreignKey: 'id'
          },
          measures: {
            service: 'diagnostic-measures',
            method: 'find',
            localKey: 'results',
            extractKey: 'measure',
            foreignKey: 'id'
          }
        }
      }
    }
  };
  if (opts.facility) query['record.facility'] = opts.facility;
  if (opts.account) query.account = opts.account;
  if (opts.action) query.action = opts.action;
  if (opts.startAt) query.createdAt = { ...query.createdAt, $gte: opts.startAt };
  if (opts.endAt) query.createdAt = { ...query.createdAt, $lte: opts.endAt };
  const result = await sdk.activityLogs().find(query);
  result.items = result.items.map(({ $populated, ...item }) => ({
    ...item,
    ...$populated,
    results: $populated.results.map(({ $populated, ...item }) => ({
      ...item,
      ...$populated
    }))
  }));
  ctx.commit('setActivityLogs', {
    items: result.items,
    total: result.total,
    paginationOpts
  });

  return result.items;
};
